/**
 * soapBubblePipeline — thin-film interference with iridescent rainbow.
 *
 * Renders a soap bubble as a circular film with thickness-based
 * interference patterns. The rainbow colors come from wavelength-
 * dependent interference, modulated by the bubble's thickness
 * variation (modeled as a smooth gradient).
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface SoapBubblePipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 3; i++) {
    v += a * valueNoise(p);
    p = p * 2.0 + vec2(1.7, 9.2);
    a *= 0.5;
  }
  return v;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected screen coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);

  // Bubble shape: a circle.
  float r = length(p);
  float bubbleRadius = 0.5 + 0.05 * sin(uTime);
  float bubbleMask = smoothstep(bubbleRadius, bubbleRadius - 0.02, r);

  // Background.
  vec3 bg = vec3(0.08, 0.10, 0.15);

  // Film thickness varies across the bubble (gravity pulls fluid down).
  float thickness = (bubbleRadius - r) / bubbleRadius * 0.5;
  // Add FBM-driven variation.
  thickness += fbm(p * 5.0 + uTime * 0.3) * 0.3;
  // Gravity: thicker at the bottom.
  thickness += (p.y + 0.3) * 0.5;
  // Audio: pulse with bass.
  thickness += bass * 0.4;

  // Interference color: convert thickness to a phase, then map to RGB.
  // Each channel peaks at a different thickness.
  float phase = thickness * 30.0;
  vec3 iridescence = vec3(
    0.5 + 0.5 * sin(phase * 1.0 + 0.0),
    0.5 + 0.5 * sin(phase * 1.1 + 2.094),
    0.5 + 0.5 * sin(phase * 1.2 + 4.189)
  );
  // Brighter at thinner regions.
  iridescence *= 0.7 + (1.0 - thickness) * 0.3;
  // Audio: shift palette.
  iridescence = mix(iridescence, iridescence.gbr, high * 0.4);
  iridescence *= 1.0 + high * 0.5;

  // Apply the bubble mask.
  vec3 col = mix(bg, iridescence, bubbleMask);

  // Bubble outline: bright rim.
  float rim = smoothstep(bubbleRadius, bubbleRadius - 0.04, r) - smoothstep(bubbleRadius - 0.04, bubbleRadius - 0.08, r);
  col += vec3(1.0) * rim * 0.8;

  // Specular highlight (sun reflection).
  vec2 sunDir = normalize(vec2(0.6, 0.8));
  float spec = exp(-dot(normalize(p + vec2(0.3)), sunDir) * 12.0);
  col += vec3(1.0, 0.95, 0.85) * spec * bubbleMask * 0.6;

  // Vignette.
  float vig = 1.0 - dot(p, p) * 0.2;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeSoapBubbleBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeSoapBubbleRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createSoapBubblePipeline(gl: WebGL2RenderingContext): SoapBubblePipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawSoapBubble(
  gl: WebGL2RenderingContext,
  pipe: SoapBubblePipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
